import { useCallback } from 'react'
import { DEFAULT_PATIENT_DATA, FORM_CONSTANTS } from '../utils/patientFormConfig'

export const usePatientForm = (
  patientDetails,
  setPatientDetails,
  makeAnonymousPatient,
  setMakeAnonymousPatient,
) => {
  const updatePatientField = useCallback(
    (field, value) => {
      setPatientDetails((prev) => ({
        ...prev,
        [field]: value,
      }))
    },
    [setPatientDetails],
  )

  const updatePatientFields = useCallback(
    (fields) => {
      setPatientDetails((prev) => ({
        ...prev,
        ...fields,
      }))
    },
    [setPatientDetails],
  )

  const updateNestedField = useCallback(
    (path, value) => {
      setPatientDetails((prev) => {
        const pathArray = path.split('.')
        const newState = { ...prev }
        let current = newState

        for (let i = 0; i < pathArray.length - 1; i++) {
          if (!current[pathArray[i]]) {
            current[pathArray[i]] = {}
          }
          current[pathArray[i]] = { ...current[pathArray[i]] }
          current = current[pathArray[i]]
        }

        current[pathArray[pathArray.length - 1]] = value
        return newState
      })
    },
    [setPatientDetails],
  )

  const handleMobileChange = useCallback(
    (value) => {
      updatePatientField('mobile', value)
    },
    [updatePatientField],
  )

  const handleNameChange = useCallback(
    (e) => {
      updatePatientField('name', e.target.value)
    },
    [updatePatientField],
  )

  const handleGenderChange = useCallback(
    (id) => {
      updatePatientField('gender', id)
    },
    [updatePatientField],
  )

  const handleAgeChange = useCallback(
    (e) => {
      const value = parseInt(e.target.value)
      if (e.target.value?.length > FORM_CONSTANTS.AGE_INPUT_MAX_LENGTH) {
        return
      }
      updatePatientField('age', !!e.target.value ? Math.max(0, value) : null)
    },
    [updatePatientField],
  )

  const handleEmailChange = useCallback(
    (e) => {
      updatePatientField('email', e.target.value.trim())
    },
    [updatePatientField],
  )

  const handleAddressChange = useCallback(
    (e) => {
      updatePatientField('address', e.target.value)
    },
    [updatePatientField],
  )

  const handleUhidChange = useCallback(
    (e) => {
      updatePatientField('uhId', e.target.value)
    },
    [updatePatientField],
  )

  const handleImportIdChange = useCallback(
    (e) => {
      updateNestedField('importData.importedFromId', e.target.value)
    },
    [updateNestedField],
  )

  const handleCityChange = useCallback(
    (e) => {
      updatePatientField('city', e.target.value)
    },
    [updatePatientField],
  )

  const handlePincodeChange = useCallback(
    (e) => {
      updatePatientField('pincode', e.target.value)
    },
    [updatePatientField],
  )

  const handleNoteChange = useCallback(
    (e) => {
      updatePatientField('note', e.target.value)
    },
    [updatePatientField],
  )

  const handleReferredByChange = useCallback(
    (e) => {
      updatePatientField('referredBy', e.target.value)
    },
    [updatePatientField],
  )

  const handleLanguageChange = useCallback(
    (e) => {
      updatePatientField('defaultCommunicationLanguage', e.target.value)
    },
    [updatePatientField],
  )

  const handleRelationChange = useCallback(
    (e) => {
      updatePatientField('dependentRelation', e.target.value)
    },
    [updatePatientField],
  )

  const handleCommunicationToAttendantChange = useCallback(
    (event) => {
      updatePatientField('communicationToAttendant', event.target.checked)
    },
    [updatePatientField],
  )

  const handleAnonymousPatientChange = useCallback(
    (checked) => {
      setMakeAnonymousPatient(checked)
      if (checked) {
        updatePatientFields({
          email: '',
          mobile: '',
        })
      }
    },
    [setMakeAnonymousPatient, updatePatientFields],
  )

  // Attendant management
  const addAttendant = useCallback(() => {
    setPatientDetails((prev) => ({
      ...prev,
      attendants: Array.isArray(prev?.attendants)
        ? [
            ...prev?.attendants,
            {
              name: '',
              primary: false,
              type: 'self', // Default from RELATIONSHIP_OPTIONS
            },
          ]
        : [
            {
              name: '',
              primary: true,
              type: 'self',
            },
          ],
    }))
  }, [setPatientDetails])

  const updateAttendant = useCallback(
    (index, field, value) => {
      setPatientDetails((prev) => ({
        ...prev,
        attendants: prev?.attendants?.map((attendant, idx) =>
          idx === index ? { ...attendant, [field]: value } : attendant,
        ),
      }))
    },
    [setPatientDetails],
  )

  const removeAttendant = useCallback(
    (index) => {
      setPatientDetails((prev) => ({
        ...prev,
        attendants: prev?.attendants?.filter((_, idx) => idx !== index),
      }))
    },
    [setPatientDetails],
  )

  // Alternative mobile numbers management
  const addAlternativeMobile = useCallback(() => {
    setPatientDetails((prev) => ({
      ...prev,
      altMobiles: Array.isArray(prev?.altMobiles) ? [...prev?.altMobiles, ''] : [''],
    }))
  }, [setPatientDetails])

  const updateAlternativeMobile = useCallback(
    (index, value) => {
      setPatientDetails((prev) => ({
        ...prev,
        altMobiles: prev?.altMobiles?.map((altMobile, idx) => (idx === index ? value : altMobile)),
      }))
    },
    [setPatientDetails],
  )

  const removeAlternativeMobile = useCallback(
    (index) => {
      setPatientDetails((prev) => ({
        ...prev,
        altMobiles: prev?.altMobiles?.filter((_, idx) => idx !== index),
      }))
    },
    [setPatientDetails],
  )

  // Communication preferences management
  const handleCommunicationToPrefChange = useCallback(
    (field, value) => {
      setPatientDetails((prev) => ({
        ...prev,
        communicationToPref: {
          toPatient: true,
          toAttendant: true,
          toOthers: true,
          receivers: [],
          ...prev?.communicationToPref,
          [field]: value,
        },
      }))
    },
    [setPatientDetails],
  )

  const addCommunicationReceiver = useCallback(() => {
    setPatientDetails((prev) => ({
      ...prev,
      communicationToPref: {
        toPatient: true,
        toAttendant: true,
        toOthers: true,
        receivers: [],
        ...prev?.communicationToPref,
        receivers: Array.isArray(prev?.communicationToPref?.receivers)
          ? [
              ...prev.communicationToPref.receivers,
              {
                toUserId: '',
                toEmail: '',
                toPhone: '',
                toWhatsAppId: '',
                toPatientId: '',
                authorizedBy: '',
                authorizedOn: '',
              },
            ]
          : [
              {
                toUserId: '',
                toEmail: '',
                toPhone: '',
                toWhatsAppId: '',
                toPatientId: '',
                authorizedBy: '',
                authorizedOn: '',
              },
            ],
      },
    }))
  }, [setPatientDetails])

  const updateCommunicationReceiver = useCallback(
    (index, field, value) => {
      setPatientDetails((prev) => ({
        ...prev,
        communicationToPref: {
          ...prev?.communicationToPref,
          receivers: prev?.communicationToPref?.receivers?.map((receiver, idx) =>
            idx === index ? { ...receiver, [field]: value } : receiver,
          ),
        },
      }))
    },
    [setPatientDetails],
  )

  const removeCommunicationReceiver = useCallback(
    (index) => {
      setPatientDetails((prev) => ({
        ...prev,
        communicationToPref: {
          ...prev?.communicationToPref,
          receivers: prev?.communicationToPref?.receivers?.filter((_, idx) => idx !== index),
        },
      }))
    },
    [setPatientDetails],
  )

  const resetForm = useCallback(() => {
    setPatientDetails(DEFAULT_PATIENT_DATA)
    setMakeAnonymousPatient(false)
  }, [setPatientDetails, setMakeAnonymousPatient])

  const getFieldValue = useCallback(
    (fieldPath) => {
      const pathArray = fieldPath.split('.')
      let value = patientDetails

      for (const path of pathArray) {
        value = value?.[path]
        if (value === undefined || value === null) break
      }

      return value
    },
    [patientDetails],
  )

  return {
    // Field update handlers
    updatePatientField,
    updatePatientFields,
    updateNestedField,

    // Specific field handlers
    handleMobileChange,
    handleNameChange,
    handleGenderChange,
    handleAgeChange,
    handleEmailChange,
    handleAddressChange,
    handleUhidChange,
    handleImportIdChange,
    handleCityChange,
    handlePincodeChange,
    handleNoteChange,
    handleReferredByChange,
    handleLanguageChange,
    handleRelationChange,
    handleCommunicationToAttendantChange,
    handleAnonymousPatientChange,

    // Array field management
    addAttendant,
    updateAttendant,
    removeAttendant,
    addAlternativeMobile,
    updateAlternativeMobile,
    removeAlternativeMobile,
    handleCommunicationToPrefChange,
    addCommunicationReceiver,
    updateCommunicationReceiver,
    removeCommunicationReceiver,

    // Utility functions
    resetForm,
    getFieldValue,
  }
}
